// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Route Planner
// Build and assign optimized hauling routes from zones to disposal sites
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  SimpleGrid, TextInput, Textarea, ActionIcon, Drawer, Table,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconTrash, IconWand,
  IconArrowRight,
} from '@tabler/icons-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';

// Fix leaflet default icon
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { primary } from '../../lib/colors';
import { WORKER_URL } from '../../lib/api-switch';
const DefaultIcon = L.icon({ iconUrl, shadowUrl: iconShadow, iconAnchor: [12, 41], popupAnchor: [1, -34] });
L.Marker.prototype.options.icon = DefaultIcon;

function makeIcon(color: string, label?: string) {
  return L.divIcon({
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    html: `<div style="width:24px;height:24px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700">${label ?? ''}</div>`,
  });
}

function FitBounds({ bounds }: { bounds: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [bounds, map]);
  return null;
}

// ── Route model (stored in component state, could persist to a table later) ──
interface RouteStop {
  id: string;
  type: 'zone' | 'site';
  entityId: number;
  name: string;
  lat: number;
  lng: number;
}

interface DebrisRoute {
  id: string;
  name: string;
  crewId: string;
  stops: RouteStop[];
  notes: string;
  createdAt: string;
}

const WORKER = WORKER_URL;

export default function RoutePlanner() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    zones, disposalSites, crews, loadZones, loadDisposalSites, loadCrews,
  } = useDebrisStore();

  const [routes, setRoutesRaw] = useState<DebrisRoute[]>(() => {
    try {
      const saved = localStorage.getItem(`debris-routes-${incidentId}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const setRoutes = (updater: DebrisRoute[] | ((prev: DebrisRoute[]) => DebrisRoute[])) => {
    setRoutesRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem(`debris-routes-${incidentId}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const [activeRoute, setActiveRoute] = useState<DebrisRoute | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [routeCrew, setRouteCrew] = useState<string | null>(null);
  const [routeNotes, setRouteNotes] = useState('');
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => { loadZones(); loadDisposalSites(); loadCrews(); }, [loadZones, loadDisposalSites, loadCrews]);

  const iZones = incidentId ? zones : [];
  const iSites = incidentId ? disposalSites : [];
  const iCrews = incidentId ? crews : [];

  const crewSelect = useMemo(() => iCrews.map(c => ({ value: String(c.id), label: c.crewName || c.driverName || `#${c.id}` })), [iCrews]);

  const zoneStops = useMemo(() =>
    iZones.filter(z => parseFloat(z.centerLat) && parseFloat(z.centerLng)).map(z => ({
      id: `zone-${z.id}`, type: 'zone' as const, entityId: z.id,
      name: z.zoneName || z.zoneCode, lat: parseFloat(z.centerLat), lng: parseFloat(z.centerLng),
    })),
  [iZones]);

  const siteStops = useMemo(() =>
    iSites.filter(s => parseFloat(s.latitude) && parseFloat(s.longitude)).map(s => ({
      id: `site-${s.id}`, type: 'site' as const, entityId: s.id,
      name: s.siteName, lat: parseFloat(s.latitude), lng: parseFloat(s.longitude),
    })),
  [iSites]);

  const allAvailable = useMemo(() => [...zoneStops, ...siteStops], [zoneStops, siteStops]);

  // Map bounds from stops
  const mapBounds = useMemo(() => {
    const pts = stops.filter(s => s.lat && s.lng).map(s => [s.lat, s.lng] as [number, number]);
    if (pts.length === 0) {
      // Show all available
      const all = allAvailable.filter(s => s.lat && s.lng).map(s => [s.lat, s.lng] as [number, number]);
      return all.length > 0 ? L.latLngBounds(all) : null;
    }
    return L.latLngBounds(pts);
  }, [stops, allAvailable]);

  const addStop = (stop: RouteStop) => {
    if (stops.find(s => s.id === stop.id)) return;
    setStops([...stops, stop]);
  };

  const removeStop = (id: string) => setStops(stops.filter(s => s.id !== id));

  const moveStop = (idx: number, dir: -1 | 1) => {
    const next = [...stops];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setStops(next);
  };

  // AI Route Optimization via Claude
  const optimizeRoute = async () => {
    if (stops.length < 3) {
      notifications.show({ message: 'Need at least 3 stops to optimize', color: '#C68700' });
      return;
    }
    setOptimizing(true);
    try {
      const prompt = `Given these debris collection stops with coordinates, reorder them for the shortest driving distance. Return ONLY a JSON array of the stop IDs in optimal order, nothing else.

Stops:
${stops.map((s, i) => `${i + 1}. ID: "${s.id}" - ${s.name} (${s.type}) at ${s.lat},${s.lng}`).join('\n')}

Rules:
- Start from the first zone stop
- End at a disposal site (type "site") if one exists in the list
- Minimize total driving distance between consecutive stops
- Return format: ["stop-id-1", "stop-id-2", ...]`;

      const res = await fetch(`${WORKER}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 1024,
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      const data = await res.json();
      const text = data?.content?.[0]?.text ?? '';

      // Extract JSON array from response
      const match = text.match(/\[[\s\S]*?\]/);
      if (match) {
        const order: string[] = JSON.parse(match[0]);
        const reordered = order
          .map(id => stops.find(s => s.id === id))
          .filter(Boolean) as RouteStop[];
        // Add any stops that weren't in the AI response
        stops.forEach(s => { if (!reordered.find(r => r.id === s.id)) reordered.push(s); });
        setStops(reordered);
        notifications.show({ message: 'Route optimized', color: 'var(--color-success)' });
      } else {
        notifications.show({ message: 'Could not parse AI response', color: '#C68700' });
      }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Optimization failed', color: 'var(--color-error)' });
    } finally { setOptimizing(false); }
  };

  const saveRoute = () => {
    if (!routeName.trim()) {
      notifications.show({ message: 'Route name is required', color: '#C68700' });
      return;
    }
    const route: DebrisRoute = {
      id: activeRoute?.id ?? `route-${Date.now()}`,
      name: routeName,
      crewId: routeCrew ?? '',
      stops: [...stops],
      notes: routeNotes,
      createdAt: activeRoute?.createdAt ?? new Date().toISOString(),
    };
    setRoutes(prev => {
      const idx = prev.findIndex(r => r.id === route.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = route; return next; }
      return [...prev, route];
    });
    setModalOpen(false);
    setActiveRoute(route);
    notifications.show({ message: 'Route saved', color: 'var(--color-success)' });
  };

  const openNew = () => {
    setActiveRoute(null);
    setRouteName('');
    setRouteCrew(null);
    setRouteNotes('');
    setStops([]);
    setModalOpen(true);
  };

  const openEdit = (route: DebrisRoute) => {
    setActiveRoute(route);
    setRouteName(route.name);
    setRouteCrew(route.crewId || null);
    setRouteNotes(route.notes);
    setStops([...route.stops]);
    setModalOpen(true);
  };

  const deleteRoute = (id: string) => {
    setRoutes(routes.filter(r => r.id !== id));
    if (activeRoute?.id === id) setActiveRoute(null);
  };

  const viewRoute = (route: DebrisRoute) => {
    setActiveRoute(route);
    setStops([...route.stops]);
  };

  const defaultCenter: [number, number] = [29.76, -95.37];

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3} c="black">Route Planner</Title>
        <Group gap="xs">
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openNew}>New Route</Button>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Route list + active route details */}
        <Stack gap="sm">
          {/* Saved routes */}
          <Paper p="md" radius="md" withBorder>
            <Text fw={700} mb="sm">Saved Routes ({routes.length})</Text>
            {routes.length === 0 ? (
              <Text size="sm" c="dimmed">No routes created yet. Click "New Route" to get started.</Text>
            ) : (
              <Stack gap="xs">
                {routes.map(r => (
                  <Paper key={r.id} p="xs" radius="sm" withBorder
                    style={{ cursor: 'pointer', border: activeRoute?.id === r.id ? '2px solid var(--color-primary-700)' : undefined }}
                    onClick={() => viewRoute(r)}>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{r.name}</Text>
                        <Text size="xs" c="dimmed">
                          {r.stops.length} stops &middot;
                          {r.stops.filter(s => s.type === 'zone').length} zones,
                          {r.stops.filter(s => s.type === 'site').length} sites
                        </Text>
                      </div>
                      <Group gap={4}>
                        <Button size="compact-xs" variant="light" onClick={e => { e.stopPropagation(); openEdit(r); }}>Edit</Button>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                          onClick={e => { e.stopPropagation(); deleteRoute(r.id); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Active route stop list */}
          {activeRoute && (
            <Paper p="md" radius="md" withBorder>
              <Text fw={700} mb="sm">Route: {activeRoute.name}</Text>
              {activeRoute.crewId && (
                <Text size="xs" c="dimmed" mb="xs">
                  Crew: {iCrews.find(c => String(c.id) === activeRoute.crewId)?.crewName ?? activeRoute.crewId}
                </Text>
              )}
              <Stack gap={4}>
                {activeRoute.stops.map((s, i) => (
                  <Group key={s.id} gap="xs">
                    <Badge size="sm" w={24} variant="light" color={s.type === 'zone' ? 'var(--color-success)' : 'var(--color-error)'}>
                      {i + 1}
                    </Badge>
                    <IconArrowRight size={12} color="#999" />
                    <Text size="sm">{s.name}</Text>
                    <Badge size="xs" variant="outline">{s.type}</Badge>
                  </Group>
                ))}
              </Stack>
              {activeRoute.notes && <Text size="xs" c="dimmed" mt="sm">{activeRoute.notes}</Text>}
            </Paper>
          )}
        </Stack>

        {/* Map */}
        <Paper withBorder style={{ height: 'calc(100vh - 260px)', minHeight: 400, borderRadius: 4, overflow: 'hidden' }}>
          <MapContainer center={defaultCenter} zoom={10} style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds bounds={mapBounds} />

            {/* Route stops with numbered markers */}
            {(activeRoute?.stops ?? stops).map((s, i) => (
              <Marker key={s.id} position={[s.lat, s.lng]}
                icon={makeIcon(s.type === 'zone' ? '#16a34a' : '#B12525', String(i + 1))}>
                <Popup>
                  <strong>Stop {i + 1}: {s.name}</strong><br />
                  Type: {s.type === 'zone' ? 'Pickup Zone' : 'Disposal Site'}
                </Popup>
              </Marker>
            ))}

            {/* Route line */}
            {(activeRoute?.stops ?? stops).length >= 2 && (
              <Polyline
                positions={(activeRoute?.stops ?? stops).map(s => [s.lat, s.lng] as [number, number])}
                pathOptions={{ color: primary[700], weight: 3, dashArray: '8 4' }}
              />
            )}

            {/* All available stops (faded) when editing */}
            {modalOpen && allAvailable.map(s => {
              if (stops.find(st => st.id === s.id)) return null;
              return (
                <Marker key={`avail-${s.id}`} position={[s.lat, s.lng]}
                  icon={makeIcon(s.type === 'zone' ? '#16a34a44' : '#B1252544')}
                  eventHandlers={{ click: () => addStop(s) }}>
                  <Popup>
                    <strong>{s.name}</strong> ({s.type})<br />
                    <em>Click to add to route</em>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </Paper>
      </SimpleGrid>

      {/* Route Builder Drawer (side panel so map stays clickable) */}
      <Drawer opened={modalOpen} onClose={() => setModalOpen(false)}
        title={activeRoute ? 'Edit Route' : 'New Route'} position="left" size="lg"
        styles={{ body: { paddingBottom: 80 } }}>
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Route Name" required value={routeName}
              onChange={e => setRouteName(e.target.value)} />
            <Select label="Assign to Crew" data={crewSelect} value={routeCrew}
              onChange={v => setRouteCrew(v)} searchable clearable />
            <Group align="flex-end">
              <Button leftSection={<IconWand size={14} />} variant="light" color="var(--data-viz-650)"
                loading={optimizing} onClick={optimizeRoute}>
                AI Optimize
              </Button>
            </Group>
          </SimpleGrid>

          <Text size="sm" fw={600}>Stops ({stops.length})</Text>
          <Text size="xs" c="dimmed">Add stops from the dropdowns or click markers on the map. Drag to reorder.</Text>

          <Group gap="sm">
            <Select placeholder="Add zone..." data={zoneStops.filter(z => !stops.find(s => s.id === z.id)).map(z => ({ value: z.id, label: z.name }))}
              onChange={v => { const s = zoneStops.find(z => z.id === v); if (s) addStop(s); }} size="xs" searchable clearable w={200} />
            <Select placeholder="Add disposal site..." data={siteStops.filter(s => !stops.find(st => st.id === s.id)).map(s => ({ value: s.id, label: s.name }))}
              onChange={v => { const s = siteStops.find(st => st.id === v); if (s) addStop(s); }} size="xs" searchable clearable w={200} />
          </Group>

          {stops.length === 0 ? (
            <Text size="sm" c="dimmed">No stops added yet</Text>
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>#</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th w={100}>Reorder</Table.Th>
                  <Table.Th w={40}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {stops.map((s, i) => (
                  <Table.Tr key={s.id}>
                    <Table.Td><Badge size="sm" variant="light">{i + 1}</Badge></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{s.name}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={s.type === 'zone' ? 'var(--color-success)' : 'var(--color-error)'}>{s.type}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={2}>
                        <Button size="compact-xs" variant="subtle" disabled={i === 0}
                          onClick={() => moveStop(i, -1)}>Up</Button>
                        <Button size="compact-xs" variant="subtle" disabled={i === stops.length - 1}
                          onClick={() => moveStop(i, 1)}>Dn</Button>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => removeStop(s.id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Textarea label="Notes" value={routeNotes} onChange={e => setRouteNotes(e.target.value)}
            autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={saveRoute}>Save Route</Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  );
}
